import React, { useState, useEffect } from 'react';
import {
	Container,
	Typography,
	Box,
	Button,
	Select,
	MenuItem,
	SnackbarOrigin,
	Dialog,
	DialogContent,
	IconButton,
	useMediaQuery,
	Theme,
	TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import OTPInput from 'react-otp-input';
import fetchHandler from '../../../../services/api/Handler';
import MyHeadings from '../Vendor-ui/Headings';
import { countryCodesdata } from '../../../../components/common/CountryData';
import MySnackbar from '../Vendor-ui/Snackbar';
import { AuthenticationAPI } from '../../../../services/api/Vendorpage/verificationvendorform';

type SnackState = {
	open: boolean;
	message: string;
	severity: 'success' | 'error' | 'info' | 'warning';
	vertical: SnackbarOrigin['vertical'];
	horizontal: SnackbarOrigin['horizontal'];
};

const countryCodes = countryCodesdata;

interface VendoRFormsProps {
	open: boolean;
	onClose: () => void;
	onOtpVerified?: () => void; // NEW callback to parent
}

const VendoRForms: React.FC<VendoRFormsProps> = ({ open, onClose }) => {
	const [formValues, setFormValues] = useState({
		countryCode: '91',
		mobile: '',
		otp: '',
	});
	const [formErrors, setFormErrors] = useState({ mobile: '', otp: '' });
	const [loadingConditions, setLoadingConditions] = useState(false);
	const [otpSent, setOtpSent] = useState(false);
	const [resendTimer, setResendTimer] = useState(0);
	const [canResend, setCanResend] = useState(true);
	const [error, setError] = useState('');
	const [snack, setOpen] = useState<SnackState>({
		open: false,
		vertical: 'top',
		horizontal: 'center',
		message: '',
		severity: 'success',
	});
	const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const currentCountry = countryCodes.find((c) => c.phone === formValues.countryCode);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
			return () => clearTimeout(timer);
		} else if (resendTimer === 0 && otpSent) {
			setCanResend(true);
		}
	}, [resendTimer, otpSent]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		let updatedValue = value;

		// Restrict only numbers for mobile field
		if (name === 'mobile') {
			updatedValue = value.replace(/\D/g, ''); // remove non-digits
			// Optional: limit to 10 digits
			if (updatedValue.length > 10) {
				updatedValue = updatedValue.slice(0, 10);
			}
		}

		setFormValues({ ...formValues, [name]: updatedValue });

		if (name === 'mobile' && formErrors.mobile) {
			setFormErrors({ ...formErrors, mobile: '' });
		}
	};

	const handleCountryCodeChange = (event: any) => {
		setFormValues({ ...formValues, countryCode: event.target.value });
	};

	const handleOtpChange = (otp: string) => {
		setFormValues({ ...formValues, otp });
		if (formErrors.otp) {
			setFormErrors({ ...formErrors, otp: '' });
		}
	};

	const validateMobile = () => {
		const errors: any = {};
		if (!formValues.mobile) {
			errors.mobile = 'Phone number is required';
		} else if (!currentCountry?.pattern.test(formValues.mobile)) {
			errors.mobile = `Phone number must be ${currentCountry?.maxLength} digits (e.g. ${currentCountry?.example})`;
		}
		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSendOtp = async () => {
		if (!validateMobile()) return;
		try {
			setLoadingConditions(true);
			const res = await AuthenticationAPI.SendOtp({
				mobile: `+${formValues.countryCode} ${formValues.mobile}`,
			});
			console.log(res);
			if (!res?.data.success) {
				setError(res?.data?.errors);
			} else {
				setOtpSent(true);
				setResendTimer(60);
				setCanResend(false);
			}
		} catch {
			toast.error('Failed to send OTP');
		} finally {
			setLoadingConditions(false);
		}
	};

	const handleResendOtp = async () => {
		if (!canResend) return;
		try {
			setLoadingConditions(true);
			const res = await AuthenticationAPI.ResendOtp({
				mobile: `+${formValues.countryCode} ${formValues.mobile}`,
			});
			if (res?.isError) {
				setError(res.error.errors);
			} else {
				setResendTimer(60);
				setCanResend(false);
			}
		} catch (error: any) {
			toast.error(error.error.errors);
		} finally {
			setLoadingConditions(false);
		}
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!formValues.otp || formValues.otp.length !== 6) {
			setFormErrors({ ...formErrors, otp: 'Please enter a valid 6-digit OTP' });
			return;
		}
		localStorage.setItem('vendorperson', `+${formValues.countryCode} ${formValues.mobile}`);
		try {
			setLoadingConditions(true);
			const payload = {
				mobile: `+${formValues.countryCode} ${formValues.mobile}`,
				otp: Number(formValues.otp),
			};
			const res = await fetchHandler({
				method: 'POST',
				endpoint: 'vendors/verify-otp',
				data: payload,
			});

			console.log(res);

			if (res.success) {
				localStorage.setItem('otp-verified', res.data.isVerified);
				// onOtpVerified(); // tell parent OTP was verified
				onClose();
			} else {
				toast.error(`${res?.errors}`);
			}
		} catch {
			toast.error(`Login failed`);
		} finally {
			setLoadingConditions(false);
		}
	};

	const maskPhoneNumber = (phone: string) => {
		if (phone.length <= 5) return phone;
		const firstTwo = phone.substring(0, 2);
		const lastThree = phone.substring(phone.length - 3);
		return `${firstTwo}${'*'.repeat(phone.length - 5)}${lastThree}`;
	};

	const handleCloseSnack = () => {
		setOpen((prev) => ({ ...prev, open: false }));
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
				<Box sx={{ m: 0, p: 2 }}>
					<IconButton
						aria-label="close"
						onClick={onClose}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							':hover': {
								color: 'black',
							},
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
				<Box>
					<Box textAlign="center" mb={2}>
						<img
							src="/assets/login-logo.png"
							alt="Login Logo"
							style={{ width: isSmallScreen ? '80px' : '100px', height: 'auto' }}
						/>
						<MyHeadings
							Title="Vendor Verification"
							subtitle="Enter your phone number to continue"
						/>
					</Box>
				</Box>
				<DialogContent sx={{ pt: 0 }}>
					<Container component="main" maxWidth="xs" sx={{ p: 0 }}>
						<form onSubmit={handleSubmit}>
							{!otpSent && (
								<Box mb={2}>
									<Typography variant="body2">Phone</Typography>
									<Box display="flex" gap={1} mt={1}>
										<Select
											value={formValues.countryCode}
											onChange={handleCountryCodeChange}
											size="small"
											sx={{ width: '100px' }}
											disabled={otpSent}
										>
											{countryCodes.map((country) => (
												<MenuItem key={country.code} value={country.phone}>
													<img
														loading="lazy"
														width="20"
														srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
														src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
														alt=""
													/>
													+{country.phone}
												</MenuItem>
											))}
										</Select>
										<TextField
											name="mobile"
											size="small"
											value={formValues.mobile}
											onChange={handleChange}
											fullWidth
											placeholder="Enter phone"
											error={!!formErrors.mobile}
											helperText={formErrors.mobile}
											disabled={otpSent}
											inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
										/>
									</Box>
									{error && (
										<Typography sx={{ color: 'red', fontSize: '12px', mt: 1 }}>
											{error}
										</Typography>
									)}
									<Button
										variant="contained"
										fullWidth
										onClick={handleSendOtp}
										sx={{ mt: 2, background: '#0C8040' }}
										disabled={!formValues.mobile || loadingConditions}
									>
										Send OTP
									</Button>
								</Box>
							)}

							{otpSent && (
								<>
									<Typography variant="body2" align="center" mb={1}>
										Enter the 6-digit code sent to +{formValues.countryCode}
										{maskPhoneNumber(formValues.mobile)}
									</Typography>
									<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
										<OTPInput
											value={formValues.otp}
											onChange={handleOtpChange}
											numInputs={6}
											inputStyle={{
												width: '2rem',
												height: '2rem',
												margin: '0 0.25rem',
												borderRadius: '4px',
												border: '1px solid rgba(0,0,0,0.3)',
											}}
											renderInput={(props) => <input {...props} />}
										/>
									</Box>
									<Button
										type="submit"
										variant="contained"
										fullWidth
										disabled={!formValues.otp || loadingConditions}
										sx={{ background: '#0C8040' }}
									>
										verification Contact
									</Button>
									<Box textAlign="center" mt={2}>
										{resendTimer > 0 ? (
											<Typography variant="caption">
												Resend OTP in {resendTimer}s
											</Typography>
										) : (
											<Button
												onClick={handleResendOtp}
												sx={{ background: '#0C8040', color: 'white' }}
												disabled={!canResend}
											>
												Resend OTP
											</Button>
										)}
									</Box>
								</>
							)}
						</form>
					</Container>
				</DialogContent>
			</Dialog>

			<MySnackbar
				open={snack?.open}
				message={snack?.message}
				severity={snack.severity}
				position={{ vertical: snack?.vertical, horizontal: snack?.horizontal }}
				onClose={handleCloseSnack}
			/>
		</>
	);
};

export default VendoRForms;
